import React from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import AvatarGroup from '@mui/material/AvatarGroup';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import Avatar from 'src/components/ui/Avatar';
import Loader from 'src/components/ui/Loader';
import Icon from 'src/components/ui/Icon';

import type { Medium, CompanyProfileQuery } from 'src/types/__generated__/graphql';
import { useCompanyMembersWithTotalQuery } from 'src/hooks/__generated__/queries';
import useAppRouter from 'src/hooks/useAppRouter';
import { routePathCompany } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

const StyledCard = styledComponent(Card)``;
StyledCard.defaultProps = {
    variant: 'outlined',
};

const StyledCardContent = styledComponent(CardContent)`
    padding: 1.5rem;
    &.MuiCardContent-root:last-child {
        padding-bottom: 1.5rem;
    }
`;
interface Props {
    company?: NonNullable<CompanyProfileQuery['companyProfile']>;
}

const CompanyMembersSection: React.FC<Props> = ({ company }) => {
    const { t } = useTranslation(['company', 'common']);
    const router = useAppRouter();
    const companyGroup = company?.capabilities?.isAbleToHaveEstateCompanies;
    const { logEventDynamic } = useDynamicLogEvents();
    const {
        data: companyMembers,
        loading,
        error,
    } = useCompanyMembersWithTotalQuery({
        variables: {
            companyId: company?.id,
            offset: 0,
            limit: 3,
            withRecursive: true,
        },
        skip: !company,
    });
    let content: React.ReactElement | null = null;

    if (loading || !companyMembers) {
        content = <Loader />;
    }
    if (error) {
        content = (
            <Box my={4}>
                <Typography variant="h2">{error.message}</Typography>
            </Box>
        );
    }

    if (!error && company && companyMembers?.companyMembersWithTotal?.results) {
        content = (
            <Box my={1}>
                <StyledCard>
                    <StyledCardContent>
                        <Typography variant="h3" serif>
                            {companyGroup ? t('company:members') : t('company:team')}
                        </Typography>
                        <Box display="flex" alignItems="center" mt={2}>
                            {companyMembers.companyMembersWithTotal.results.length > 0 && (
                                <Box mr={1}>
                                    <AvatarGroup max={3}>
                                        {companyMembers.companyMembersWithTotal.results.map(
                                            (member) =>
                                                member && (
                                                    <Avatar
                                                        key={member.id}
                                                        alt={member.fullName}
                                                        image={member.image as Medium}
                                                        fallbackText={member.initials}
                                                        size={40}
                                                    />
                                                ),
                                        )}
                                    </AvatarGroup>
                                </Box>
                            )}
                            <Box display="flex" flexDirection="column">
                                <Typography variant="h5">
                                    {company.numberOfMembersRecursively === 0 && t('company:noMember')}
                                    {companyGroup
                                        ? Number(company.numberOfMembersRecursively) >= 1 &&
                                          t('company:membersWithCount', {
                                              count: Number(company.numberOfMembersRecursively),
                                              plurial: Number(company.numberOfMembersRecursively) > 1 ? 's' : '',
                                          })
                                        : Number(company.numberOfMembersRecursively) >= 1 &&
                                          t('company:personWhoWorkHere', {
                                              count: Number(company.numberOfMembersRecursively),
                                              plurial: Number(company.numberOfMembersRecursively) > 1 ? 's' : '',
                                              conjugation: Number(company.numberOfMembersRecursively) > 1 ? 'ent' : 'e',
                                              person:
                                                  Number(company.numberOfMembersRecursively) === 1
                                                      ? 'person'
                                                      : 'people',
                                          })}
                                </Typography>
                                {Number(company.numberOfMembersRecursively) > 0 && (
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="center"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            logEventDynamic(AnalyticsEvent.Team);
                                            // eslint-disable-next-line -- no need to await router
                                            router.replace(
                                                `${routePathCompany}/${company.id}/team?anchor=team-section`,
                                            );
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {companyGroup ? t('company:seeGroupMembers') : t('company:seeAllTeam')}
                                        </Typography>
                                        <Box ml="0.5rem">
                                            <Icon size="0.8rem" name="ArrowRight" />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </StyledCardContent>
                </StyledCard>
            </Box>
        );
    }
    return content;
};

export default CompanyMembersSection;
